const DEFAULT_MESSAGE = 'Tente novamente em instantes.';

/**
 * Mensagem amigável de uma falha da Api.
 *
 * O interceptor de [src/Api.js](../Api.js) rejeita com o corpo da resposta quando o backend
 * devolve HTTP 200 com `status: 'error'`, e com o `AxiosError` quando o status é de erro —
 * por isso as duas formas são cobertas aqui.
 */
export function getApiErrorMessage(error: unknown, fallback: string = DEFAULT_MESSAGE): string {
	const candidate = error as
		| { status?: string; message?: string; data?: unknown; response?: { data?: { message?: string } } }
		| null
		| undefined;

	const fromResponse = candidate?.response?.data?.message;
	if (typeof fromResponse === 'string' && fromResponse.trim()) {
		return fromResponse;
	}

	// `message` só é aproveitado quando o objeto rejeitado é o ENVELOPE do backend — o
	// interceptor de `src/Api.js` rejeita com `response.data`, que traz `status: 'error'`.
	// Confiar em qualquer `.message` punha texto técnico na tela do usuário: um `AxiosError`
	// sem resposta (queda de conexão) chega como `'Network Error'`, timeout como
	// `'timeout of 0ms exceeded'`, e um `TypeError` lançado dentro do próprio `try` como
	// `"Cannot read properties of undefined..."` — tudo em inglês, dentro do `swal`. O
	// CLAUDE.md é explícito: detalhe técnico vai para `console.error`, não para a mensagem.
	const isBackendEnvelope = !(error instanceof Error) && candidate?.status === 'error';
	if (isBackendEnvelope && typeof candidate?.message === 'string' && candidate.message.trim()) {
		return candidate.message;
	}

	if (typeof candidate?.data === 'string' && candidate.data.trim()) {
		return candidate.data;
	}

	return fallback;
}

/**
 * Código (`code`) que o backend mandou numa falha da Api, nas mesmas duas formas de rejeição:
 * corpo da resposta de erro HTTP (`response.data.code`) ou envelope `status: 'error'` com HTTP 200.
 *
 * O `code` do próprio `AxiosError` (ex.: `ERR_BAD_RESPONSE`) não vem do backend e é ignorado: lido
 * primeiro, ele escondia o código do corpo (MF-1143).
 */
export function getApiErrorCode(error: unknown): string | undefined {
	const candidate = error as { status?: string; code?: unknown; response?: { data?: { code?: unknown } } } | null | undefined;

	const fromResponse = candidate?.response?.data?.code;
	if (typeof fromResponse === 'string' && fromResponse) {
		return fromResponse;
	}

	const isBackendEnvelope = !(error instanceof Error) && candidate?.status === 'error';
	if (isBackendEnvelope && typeof candidate?.code === 'string' && candidate.code) {
		return candidate.code;
	}

	return undefined;
}

/**
 * A exportação foi recusada por falta da permissão do menu (MF-1143): Financeiro responde `FORBIDDEN` e
 * Relatórios, `REPORT_FORBIDDEN`.
 */
export function isExportPermissionDenied(error: unknown): boolean {
	const code = getApiErrorCode(error);
	return code === 'FORBIDDEN' || code === 'REPORT_FORBIDDEN';
}
